import React, { useState } from 'react'; 
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography'; // Import Typography
import { varAlpha } from 'src/theme/styles';
import { Iconify } from '../iconify';
import { uploadClasses } from './classes';
import { UploadPlaceholder } from './components/placeholder';
import { RejectionFiles } from './components/rejection-files';
import { MultiFilePreview } from './components/preview-multi-file';
import { DeleteButton, SingleFilePreview } from './components/preview-single-file';
import CircularProgress from '@mui/material/CircularProgress'; 
import { useTranslation } from 'react-i18next';

export function Upload({
  sx,
  value,
  error,
  disabled,
  onDelete,
  onUpload,
  onRemove,
  thumbnail,
  helperText,
  onRemoveAll,
  multiple = false,
  className,
  setFiles, 
  mode,
  isUploading,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    onDrop: (acceptedFiles) => {
      const fileData = acceptedFiles.map((file) => ({
        name: file.name,
        fileId: file.id,
        fullPath: URL.createObjectURL(file),
      }));

      setFiles(fileData); 
    },
    ...other,
  });
  const { t } = useTranslation("dashboard/upload");
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const isArray = Array.isArray(value) && multiple;
  const hasFile = !isArray && !!value;
  const hasFiles = isArray && !!value.length;

  const hasError = !!error; 
  const renderMultiPreview = hasFiles && (
    <>
      <MultiFilePreview files={value} onRemove={onRemove} sx={{ my: 3 }} />
      {(onRemoveAll || onUpload) && (
        <Box gap={1.5} display="flex" justifyContent="flex-end">
          {mode !== 'loyalty' && onRemoveAll && (
            <Button color="inherit" variant="outlined" size="small" onClick={onRemoveAll}>
             {t("button.remove_all")}  </Button>
          )}
  
          {onUpload && (
            <Button
              size="small"
              variant="contained"
              onClick={onUpload}
              startIcon={<Iconify icon="eva:cloud-upload-fill" />}
            >
              {t("button.upload")} 
            </Button>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box
      className={uploadClasses.upload.concat(className ? ` ${className}` : '')}
      sx={{ width: 1, position: 'relative', ...sx }}
    >
      <Box
        {...getRootProps()}
        sx={{
          p: 5,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
          border: (theme) => `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': { opacity: 0.72 },
          ...(isDragActive && { opacity: 0.72 }),
          ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
          ...(hasError && {
            color: 'error.main',
            borderColor: 'error.main',
            bgcolor: (theme) => varAlpha(theme.vars.palette.error.mainChannel, 0.08),
          }),
          ...(hasFile && { padding: '28% 0' }),
        }}
      >
        <input {...getInputProps()} />
        {hasFile ? <SingleFilePreview file={value} /> : <UploadPlaceholder />}
      </Box>
  
      {hasFile && <DeleteButton onClick={onDelete} />}
      {helperText && (
        <FormHelperText error={!!error} sx={{ px: 2 }}>
          {helperText}
        </FormHelperText>
      )}
      <RejectionFiles files={fileRejections} />
      {hasError && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
  
  {isUploading ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',  
        alignItems: 'center',      
        padding: 2,               
        height: '100%',          
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    renderMultiPreview
  )}

    </Box>
  );
}
